import output from "_src/engine/output";
import {DEFAULT_ATTRIBUTES, DEFAULT_NO_INDICES_ATTRIBUTES} from "_src/engine/utility/attributes";

import appState from "_src/app_state";
import {DEPENDENT_BUFFER_NAMES, Geometry, GeometryBuffer} from "_src/engine/geometry";
import config from "_src/config";

let _db = null;

let _initPromise = null;

export default {

  async init() {

    const promise = new Promise((resolve, reject) => {

      const indexedDB = _getDataBase();

      if (!indexedDB) {
        reject();
      }

      const openRequest = indexedDB.open("webstudio_store", 10);

      openRequest.onupgradeneeded = () => {
        _db = openRequest.result;
        _db.createObjectStore("ws_cached_data");
      };

      openRequest.onerror = () => {
        reject(openRequest.error);
      };

      openRequest.onsuccess = () => {

        _db = openRequest.result;

        _db.onerror = function(e) {
          output.error(output.STORAGE_ERROR, e.target.error.message);
        };

        _initPromise = null;
        resolve();
      };

    });

    _initPromise = promise;

    try {
      await promise;
      return true;
    } catch (error) {
      output.error(output.STORAGE_ERROR, "Can't initialize cache: " + error);
      return false;
    }

  },

  putFrameData(frame) {

    const chunks = frame.model.chunks;
    const batch = {};

    let chunksCounter = 0;

    for (let i = 0; i < chunks.length; ++i) {

      const chunk = chunks[i];

      if (chunk.isEmpty()) {
        continue;
      }

      const geometry = chunk.geometry;
      const layout = geometry.getLayout();

      const positions = geometry.getItemBuffer("a_position");
      if (!positions) {
        continue;
      }

      const chunkData = {
        attributes : {},
        bbox       : geometry.bbox,
      };

      const savingAttributes = geometry.isPointCloud ? DEFAULT_NO_INDICES_ATTRIBUTES : DEFAULT_ATTRIBUTES;
      for (const name of savingAttributes) {
        if (name in layout) {
          const attr = layout[name];
          chunkData.attributes[name] = {
            buffer : attr.buffer.slice(),
            type   : attr.type
          };
        }
      }

      const key = frame.cacheID + "_" + chunk.getID();
      batch[key] = chunkData;

      ++chunksCounter;

    }

    if (chunksCounter > 0) {
      appState.setStateValue("cachingObjectsNumber", 1);
      return _putPackage(batch).then(() => {
        appState.setStateValue("cachingObjectsNumber", -1);
      });
    } else {
      return null;
    }

  },

  async getFrameData(frame) {
    const keys = [];
    const ids = [];

    for (const chunk of frame.model.chunks) {
      if (chunk.geometry) {
        continue;
      }
      keys.push(frame.cacheID + "_" + chunk.getID());
      ids.push(chunk.getID());
    }

    try {
      const batch = await _getPackage(keys);

      for (let i = 0; i < batch.length; ++i) {
        const chunkData = batch[i];
        if (!chunkData) return false;
        const chunk = frame.getChunkByID(ids[i]);

        const layout = {};
        const attributes = chunkData.attributes;

        for (let name in attributes) {
          const buffer = attributes[name].buffer;
          const type = attributes[name].type;
          layout[name] = new GeometryBuffer({ buffer : buffer, type : type });
          if (config.viewOnlyMode && !DEPENDENT_BUFFER_NAMES.includes(name)) {
            layout[name].scheduleUnload();
          }
        }

        const geometry = new Geometry({
          bbox     : chunkData.bbox,
          layout   : layout,
        });

        frame.model.setChunkGeometry(chunk, geometry);
        frame.model.updateChunkBBox(chunk);
        chunk.updateSelectionMask();
      }
    } catch (error) {
      output.error(output.STORAGE_ERROR, "Can't get buffer from cache: " + error);
      return false;
    }

    frame.loaded = true;
    return true;
  },

  async cleanup() {
    await _clearDataBase(_db, "ws_cached_data");
  },

}

function _getDataBase() {
  return window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB;
}

function _clearDataBase(db, name) {
  return new Promise((resolve, reject) => {

    const transaction = db.transaction(name, "readwrite");
    const storage = transaction.objectStore(name);

    const request = storage.getAllKeys();

    request.onsuccess = () => {

      const keyList = request.result;

      const promises = [];
      for (let key of keyList) {
        const promise = new Promise((resolve, reject) => {
          const deleteRequest = storage.delete(key);
          deleteRequest.onsuccess = () => {
            resolve();
          };
          deleteRequest.onerror = () => {
            reject(deleteRequest.error);
          };
        });
        promises.push(promise);
      }
      Promise.all(promises).then(() => {
        resolve();
      }).catch(() => {
        reject();
      });
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

async function _getPackage(keys) {
  if (_initPromise) {
    await _initPromise;
  }

  return new Promise((resolve, reject) => {

    const transaction = _db.transaction(["ws_cached_data"], "readonly");
    const storage = transaction.objectStore("ws_cached_data");

    const result = [];

    for (let i = 0; i < keys.length; ++i) {

      const key = keys[i];
      const request = storage.get(key);

      request.onsuccess = function() {
        result[i] = request.result;
      };

      request.onerror = function() {
        result[i] = null;
      };

    }

    transaction.oncomplete = function() {
      resolve(result);
    };

    transaction.onerror = function() {
      reject("Couldn't get data from disk.");
    };

  });
}

async function _putPackage(batch) {
  if (_initPromise) {
    await _initPromise;
  }

  return new Promise((resolve, reject) => {

    const transaction = _db.transaction(["ws_cached_data"], "readwrite");
    const storage = transaction.objectStore("ws_cached_data");

    for (let key in batch) {
      storage.put(batch[key], key);
    }

    transaction.oncomplete = function() {
      resolve();
    };

    transaction.onerror = function() {
      reject("Couldn't put data on disk.");
    };

  });
}

/*
async function _put(binary, key) {
  if (_initPromise) {
    await _initPromise;
  }

  return new Promise((resolve, reject) => {

    const transaction = _db.transaction(["ws_cached_data"], "readwrite");
    const storage = transaction.objectStore("ws_cached_data");

    const request = storage.put(binary, key);

    request.onsuccess = function() {
      resolve();
    };

    request.onerror = function() {
      reject("Couldn't put data on disk.");
    };

  });
}

function _putChunkData(chunk, key) {
  if (chunk.isEmpty()) {
    return Promise.resolve();
  }

  const geometry = chunk.geometry;
  const layout = geometry.getLayout();

  const positions = geometry.getItemBuffer("a_position");
  const indices = geometry.getItemBuffer("indices");

  if (!positions || !indices) {
    return Promise.resolve();
  }

  const chunkData = {
    attributes : {},
    bbox       : geometry.bbox,
  };

  for (let name of DEFAULT_ATTRIBUTES) {
    if (name in layout) {
      const attr = layout[name];
      chunkData.attributes[name] = {
        buffer : attr.buffer.slice(),
        type   : attr.type
      };
    }
  }

  return _put(chunkData, key);
}

async function _add(binary, key) {
  if (_initPromise) {
    await _initPromise;
  }

  return new Promise((resolve, reject) => {

    const transaction = _db.transaction("ws_cached_data", "readwrite");
    const storage = transaction.objectStore("ws_cached_data");

    const request = storage.add(binary, key);

    request.onsuccess = function() {
      resolve();
    };

    request.onerror = function() {
      reject(request.error);
    };

  });
}

async function _deletePackage(keys) {
  if (_initPromise) {
    await _initPromise;
  }

  return new Promise((resolve, reject) => {

    const transaction = _db.transaction(["ws_cached_data"], "readwrite");
    const storage = transaction.objectStore("ws_cached_data");

    const result = [];

    for (let i = 0; i < keys.length; ++i) {
      storage.delete(keys[i]);
    }

    transaction.oncomplete = function() {
      resolve(result);
    };

    transaction.onerror = function() {
      reject("Couldn't delete data from disk.");
    };

  });
}

async function _get(key) {
  if (_initPromise) {
    await _initPromise;
  }

  return new Promise((resolve, reject) => {

    const transaction = _db.transaction("ws_cached_data", "readonly");
    const storage = transaction.objectStore("ws_cached_data");

    const request = storage.get(key);

    request.onsuccess = function() {
      resolve(request.result);
    };

    request.onerror = function() {
      reject("Couldn't get data from disk.");
    };

  });
}

async function _getAllKeys() {
  if (_initPromise) {
    await _initPromise;
  }

  return new Promise((resolve, reject) => {

    const transaction = _db.transaction("ws_cached_data", "readonly");
    const storage = transaction.objectStore("ws_cached_data");

    const request = storage.getAllKeys();

    request.onsuccess = function() {
      resolve(request.result);
    };

    request.onerror = function() {
      reject(request.error);
    };

  });
}

function _checkExists(key) {
  return _getAllKeys().then((keys) => {
    return keys.indexOf(key) != -1;
  });
}

async function _delete(key) {
  if (_initPromise) {
    await _initPromise;
  }

  return new Promise((resolve, reject) => {

    const transaction = _db.transaction("ws_cached_data", "readwrite");
    const storage = transaction.objectStore("ws_cached_data");

    const request = storage.delete(key);

    request.onsuccess = function() {
      resolve();
    };

    request.onerror = function() {
      reject(request.error);
    };

  });
}
*/
